import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { printHtmlSilent } from "@/lib/kioskMode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";
import {
  Users,
  UserPlus,
  Pencil,
  Trash2,
  ShieldCheck,
  KeyRound,
  Save,
  X as XIcon,
  Printer,
} from "lucide-react";

export const ROLE_BADGE = {
  super_manager: "bg-purple-100 text-purple-800 border-purple-300",
  manager:       "bg-emerald-100 text-emerald-800 border-emerald-300",
  cashier:       "bg-blue-100 text-blue-800 border-blue-300",
  waiter:        "bg-amber-100 text-amber-800 border-amber-300",
  driver:        "bg-slate-100 text-slate-700 border-slate-300",
};

export function emptyPermissions(catalog) {
  const o = {};
  catalog.forEach((f) => (o[f.key] = false));
  return o;
}

export function PermissionGrid({ value, onChange, catalog, testidPrefix = "perm" }) {
  // Render the 16+ toggles in 2 columns for compactness.
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
      {catalog.map((f) => (
        <label
          key={f.key}
          className="flex items-center justify-between gap-3 py-1.5 px-2 rounded border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer"
          data-testid={`${testidPrefix}-row-${f.key}`}
        >
          <span className="text-sm flex-1">{f.label}</span>
          <Switch
            checked={!!value[f.key]}
            onCheckedChange={(v) => onChange({ ...value, [f.key]: !!v })}
            data-testid={`${testidPrefix}-toggle-${f.key}`}
          />
        </label>
      ))}
    </div>
  );
}

export default function OperatorsTab({ currentUser }) {
  // Catalog ------------------------------------------------------------
  const [catalog, setCatalog] = useState({ fields: [], roles: [] });
  // Users list ---------------------------------------------------------
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  // Form state ---------------------------------------------------------
  const [editing, setEditing] = useState(null); // null = create dialog closed
  const [form, setForm] = useState({
    name: "",
    pin: "",
    role: "cashier",
    permissions: {},
  });

  // Role-templates state ----------------------------------------------
  const [templates, setTemplates] = useState({}); // role → permissions

  // Delete confirmation -----------------------------------------------
  const [delConfirm, setDelConfirm] = useState(null);

  const canManage = currentUser && ["manager", "super_manager"].includes(currentUser.role);

  // Load catalog + users + templates
  const reload = async () => {
    try {
      const [cat, u, tpl] = await Promise.all([
        http.get("/permissions/catalog"),
        http.get("/users"),
        http.get("/role-templates"),
      ]);
      setCatalog(cat.data);
      setUsers(u.data || []);
      const tplMap = {};
      (tpl.data || []).forEach((t) => { tplMap[t.role] = t.permissions; });
      setTemplates(tplMap);

    } catch (e) {
      toast.error("Σφάλμα φόρτωσης χρηστών");
    }
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- New user dialog ----------
  const openCreate = () => {
    const tpl = templates["cashier"] || emptyPermissions(catalog.fields);
    setEditing({ id: null });
    setForm({
      name: "",
      pin: "",
      role: "cashier",
      permissions: { ...tpl },
    });
  };
  const openEdit = (u) => {
    setEditing({ id: u.id });
    setForm({
      name: u.name || "",
      pin: "",
      role: u.role,
      permissions: { ...(u.permissions || emptyPermissions(catalog.fields)) },
    });
  };
  const closeDialog = () => setEditing(null);

  // When role changes inside the form, refresh permissions from template
  // (so the operator gets a starting point — they can still tweak).
  const onRoleChange = (newRole) => {
    setForm((f) => ({
      ...f,
      role: newRole,
      permissions: { ...(templates[newRole] || emptyPermissions(catalog.fields)) },
    }));
  };

  const saveUser = async () => {
    if (!form.name.trim()) { toast.error("Το όνομα είναι υποχρεωτικό"); return; }
    if (!editing.id && (!form.pin || form.pin.length < 4)) {
      toast.error("PIN τουλάχιστον 4 ψηφία"); return;
    }
    setBusy(true);
    try {
      if (editing.id) {
        const payload = {
          name: form.name,
          role: form.role,
          permissions: form.permissions,
        };
        if (form.pin && form.pin.length >= 4) payload.pin = form.pin;
        await http.put(`/users/${editing.id}`, payload);
        toast.success("Ενημερώθηκε");
      } else {
        await http.post("/users", {
          name: form.name,
          pin: form.pin,
          role: form.role,
          permissions: form.permissions,
        });
        toast.success("Δημιουργήθηκε νέος χρήστης");
      }
      closeDialog();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async () => {
    if (!delConfirm) return;
    setBusy(true);
    try {
      await http.delete(`/users/${delConfirm.id}`);
      toast.success("Διαγράφηκε");
      setDelConfirm(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally { setBusy(false); }
  };

  // ---------- Print operator label («ετικέτα χειριστή») ----------
  // SEC-004: the server no longer stores the plaintext PIN. To (re)print
  // the operator's login barcode «88888XXXX», the manager must supply a
  // fresh PIN which resets the hash server-side and returns a one-time
  // ``barcode`` string used only for this print session.
  const printLabel = async (u) => {
    // eslint-disable-next-line no-alert
    const pin = (await promptDialog(
      `Δώσε νέο PIN 4-12 ψηφίων για τον χρήστη «${u.name}».\n\n` +
      "Ο σέρβερ δεν αποθηκεύει πλέον το PIN σε καθαρό κείμενο (SEC-004).\n" +
      "Αυτή η ενέργεια θα ορίσει το PIN και θα εκτυπώσει τη νέα ετικέτα."
    ));
    if (!pin) return;
    if (!/^\d{4,12}$/.test(pin)) {
      toast.error("Το PIN πρέπει να είναι 4-12 ψηφία");
      return;
    }
    setBusy(true);
    try {
      const { data: putResp } = await http.put(`/users/${u.id}`, { pin });
      if (!putResp?.barcode) {
        toast.error("Ο σέρβερ δεν επέστρεψε barcode");
        return;
      }
      const { data } = await http.get(`/users/${u.id}/print-info`);
      const barcode = putResp.barcode;
      // Pre-render barcode SVG in parent + inline into iframe HTML.
      const { default: JsBarcode } = await import("jsbarcode");
      const bcSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      try {
        JsBarcode(bcSvg, barcode, {
          format: "CODE128", width: 1, height: 30, displayValue: false, margin: 0,
        });
      } catch (err) {
        /* eslint-disable-next-line no-console */
        console.error("JsBarcode failed:", err);
      }
      bcSvg.setAttribute("class", "bc");
      const html = `<!doctype html><html lang="el"><head><meta charset="utf-8" />
<title>${(data.name || "").replace(/</g, "&lt;")} — ετικέτα</title>
<style>
  @page { size: 40mm 25mm landscape; margin: 0; }
  html, body { margin: 0; padding: 0; background:#fff; color:#000;
    font-family: "Helvetica","Arial",sans-serif; }
  .lbl { width: 40mm; padding: 1.5mm 2mm; box-sizing: border-box;
         display: flex; flex-direction: column; align-items: center;
         justify-content: space-between;
         margin-top: 15mm; }
  .name { font-size: 11pt; font-weight: 800; letter-spacing: .02em;
          text-align: center; margin: 0; line-height: 1.1;
          word-break: break-word; }
  .role { font-size: 5pt; font-weight: 600; text-transform: uppercase;
          letter-spacing: .1em; margin-top: 1mm; }
  svg.bc { width: 100%; height: 7mm; margin-top: 1mm; }
  @media print { .lbl { padding: 1mm 1.5mm; } }
</style></head><body>
  <div class="lbl">
    <div class="name">${(data.name || "").replace(/</g, "&lt;")}</div>
    <div class="role">${(data.role_label || "").replace(/</g, "&lt;")}</div>
    ${bcSvg.outerHTML}
  </div>
</body></html>`;
      printHtmlSilent(html, { afterMs: 200 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία εκτύπωσης ετικέτας");
    } finally {
      setBusy(false);
    }
  };

  // ---------- Print driver card («κάρτα οδηγού») ----------
  // Twin of `printLabel` but renders the *driver* barcode (8888XXXX —
  // the Code128 the Driver Hub scans for batch order-assignment). Only
  // available when the user has role=driver. Same 40×25mm label format
  // for consistency with the operator-login label.
  const printDriverCard = async (u) => {
    setBusy(true);
    try {
      const { data } = await http.get(`/users/${u.id}/print-info`);
      if (!data.driver_barcode) {
        toast.error("Δεν έχει ακόμα κωδικό οδηγού. Άνοιξε τη λίστα οδηγών στο Delivery μία φορά για να δημιουργηθεί.");
        return;
      }
      const { default: JsBarcode } = await import("jsbarcode");
      const bcSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      try {
        JsBarcode(bcSvg, data.driver_barcode, {
          format: "CODE128", width: 1, height: 30, displayValue: false, margin: 0,
        });
      } catch (err) {
        /* eslint-disable-next-line no-console */
        console.error("JsBarcode failed:", err);
      }
      bcSvg.setAttribute("class", "bc");
      const html = `<!doctype html><html lang="el"><head><meta charset="utf-8" />
<title>${(data.name || "").replace(/</g, "&lt;")} — κάρτα οδηγού</title>
<style>
  @page { size: 40mm 25mm landscape; margin: 0; }
  html, body { margin: 0; padding: 0; background:#fff; color:#000;
    font-family: "Helvetica","Arial",sans-serif; }
  .lbl { width: 40mm; padding: 1.5mm 2mm; box-sizing: border-box;
         display: flex; flex-direction: column; align-items: center;
         justify-content: space-between; margin-top: 15mm; }
  .name { font-size: 11pt; font-weight: 800; letter-spacing: .02em;
          text-align: center; margin: 0; line-height: 1.1;
          word-break: break-word; }
  .role { font-size: 5pt; font-weight: 600; text-transform: uppercase;
          letter-spacing: .1em; margin-top: 1mm; color:#047857; }
  svg.bc { width: 100%; height: 7mm; margin-top: 1mm; }
  .code { font-size: 6pt; font-family: monospace; margin-top: 0.5mm; }
  @media print { .lbl { padding: 1mm 1.5mm; } }
</style></head><body>
  <div class="lbl">
    <div class="name">${(data.name || "").replace(/</g, "&lt;")}</div>
    <div class="role">ΚΑΡΤΑ ΟΔΗΓΟΥ</div>
    ${bcSvg.outerHTML}
    <div class="code">${data.driver_barcode}</div>
  </div>
</body></html>`;
      printHtmlSilent(html, { afterMs: 200 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία εκτύπωσης κάρτας");
    } finally {
      setBusy(false);
    }
  };

  // ---------- Templates ----------
  // (Το UI «Defaults Ρόλων» μεταφέρθηκε στο tab Δικαιώματα — RoleDefaultsCard.
  //  Τα templates φορτώνονται ακόμα εδώ για το prefill νέων χειριστών.)

  const roleLabel = (code) =>
    catalog.roles.find((r) => r.code === code)?.label ||
    (code === "super_manager" ? "Σούπερ Μάνατζερ" : code);

  if (!canManage) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 text-slate-600">
          <ShieldCheck size={20} />
          <span>Δεν έχετε δικαίωμα διαχείρισης χειριστών.</span>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="operators-tab">
      {/* Users list -------------------------------------------------- */}
      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users size={20} className="text-blue-600" />
            <h3 className="font-bold text-lg">Χειριστές ({users.length})</h3>
          </div>
          <Button
            onClick={openCreate}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="users-create-btn"
          >
            <UserPlus size={16} className="mr-1.5" />
            Νέος Χειριστής
          </Button>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Όνομα</th>
                <th className="text-left px-3 py-2">Ρόλος</th>
                <th className="text-center px-3 py-2">Ενεργός</th>
                <th className="text-right px-3 py-2 w-40">Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2 font-medium">
                    {u.name}
                    {u.hidden && (
                      <span className="ml-2 text-[10px] text-purple-700 uppercase font-mono">κρυφός</span>
                    )}
                    {u.role === "driver" && u.driver_barcode && (
                      <div
                        className="text-[10px] font-mono text-slate-600 mt-0.5"
                        title="Κωδικός κάρτας οδηγού (barcode 8888XXXX)"
                        data-testid={`user-driver-barcode-${u.id}`}
                      >
                        <span className="inline-block px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-800 font-bold">
                          {u.driver_barcode}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs border ${ROLE_BADGE[u.role] || ROLE_BADGE.cashier}`}>
                      {roleLabel(u.role)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={u.active ? "text-emerald-700" : "text-slate-400"}>
                      {u.active ? "✓" : "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => printLabel(u)}
                      disabled={busy || u.role === "super_manager"}
                      className="text-blue-700 hover:bg-blue-50"
                      title="Εκτύπωση ετικέτας χειριστή"
                      data-testid={`user-print-${u.id}`}
                    >
                      <Printer size={14} />
                    </Button>
                    {u.role === "driver" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printDriverCard(u)}
                        disabled={busy}
                        className="text-emerald-700 hover:bg-emerald-50"
                        title="Εκτύπωση κάρτας οδηγού (barcode 8888XXXX)"
                        data-testid={`user-print-driver-card-${u.id}`}
                      >
                        <Printer size={14} />
                        <span className="ml-1 text-[10px] font-bold">Οδηγού</span>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(u)}
                      data-testid={`user-edit-${u.id}`}
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => setDelConfirm(u)}
                      disabled={u.role === "super_manager"}
                      data-testid={`user-delete-${u.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={4} className="text-center py-6 text-slate-400">Δεν υπάρχουν χρήστες.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Role defaults: μεταφέρθηκε στο tab «Δικαιώματα» (Ιούν 2026) */}

      {/* Create / Edit dialog ------------------------------------- */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-2xl" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing?.id ? "Επεξεργασία Χειριστή" : "Νέος Χειριστής"}
            </DialogTitle>
            <DialogDescription>
              {editing?.id
                ? "Άλλαξε όνομα, ρόλο, PIN ή δικαιώματα."
                : "Συμπλήρωσε στοιχεία και επίλεξε ρόλο — τα δικαιώματα γεμίζουν αυτόματα από το template."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-bold">Όνομα</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="π.χ. Γιώργος"
                  data-testid="user-form-name"
                />
              </div>
              <div>
                <Label className="text-xs font-bold flex items-center gap-1">
                  <KeyRound size={12} /> PIN {editing?.id && <span className="text-slate-400 text-[10px]">(προαιρετικό)</span>}
                </Label>
                <Input
                  type="password"
                  value={form.pin}
                  onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })}
                  placeholder={editing?.id ? "Άφησε κενό για να μη αλλάξει" : "4-12 ψηφία"}
                  maxLength={12}
                  data-testid="user-form-pin"
                />
              </div>
              <div>
                <Label className="text-xs font-bold">Ρόλος</Label>
                <Select value={form.role} onValueChange={onRoleChange}>
                  <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {catalog.roles.map((r) => (
                      <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold mb-1.5 block">Δικαιώματα ({Object.values(form.permissions).filter(Boolean).length}/{catalog.fields.length})</Label>
              <PermissionGrid
                value={form.permissions}
                onChange={(p) => setForm({ ...form, permissions: p })}
                catalog={catalog.fields}
                testidPrefix="user-form-perm"
              />
            </div>

            {/* Driver barcode info (read-only) — visible only when the
                role is «Οδηγός». Lets the admin verify / print the
                Driver-Hub card without leaving this form. */}
            {form.role === "driver" && editing?.id && editing?.driver_barcode && (
              <div className="border-2 border-blue-200 bg-blue-50 rounded-md p-3 flex items-center gap-3" data-testid="user-form-driver-barcode-box">
                <Printer size={20} className="text-blue-700 shrink-0" />
                <div className="flex-1">
                  <div className="text-xs font-bold text-blue-900">Κάρτα Οδηγού</div>
                  <div className="text-[10px] text-blue-700 leading-snug">
                    Barcode για σάρωση από το Driver Hub στο Delivery.
                  </div>
                  <div className="font-mono font-bold text-base mt-0.5 text-blue-900">
                    {editing.driver_barcode}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => printDriverCard(editing)}
                  className="bg-white border-blue-300 hover:bg-blue-100"
                  data-testid="user-form-print-driver-card"
                >
                  <Printer size={14} className="mr-1" /> Εκτύπωση
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} data-testid="user-form-cancel">
              <XIcon size={14} className="mr-1" />
              Άκυρο
            </Button>
            <Button
              onClick={saveUser}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="user-form-save"
            >
              <Save size={14} className="mr-1" />
              {editing?.id ? "Αποθήκευση" : "Δημιουργία"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation -------------------------------------- */}
      <Dialog open={!!delConfirm} onOpenChange={(o) => !o && setDelConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="user-delete-dialog">
          <DialogHeader>
            <DialogTitle>Διαγραφή Χειριστή</DialogTitle>
            <DialogDescription>
              Θα διαγραφεί ο χρήστης <strong>{delConfirm?.name}</strong> ({delConfirm?.role ? roleLabel(delConfirm.role) : ""}).
              Η ενέργεια είναι μη επιστρέψιμη.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelConfirm(null)} data-testid="user-delete-cancel">
              Άκυρο
            </Button>
            <Button
              onClick={deleteUser}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="user-delete-confirm"
            >
              <Trash2 size={14} className="mr-1" />
              Διαγραφή
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
